import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Edit, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import type { TestCaseWithEvaluations } from '@shared/types/evaluation'

interface SortableTestCaseItemProps {
  testCase: TestCaseWithEvaluations
  index: number
  onEdit: (testCase: TestCaseWithEvaluations) => void
  onDelete: (testCase: TestCaseWithEvaluations) => void
}

export const SortableTestCaseItem: React.FC<SortableTestCaseItemProps> = ({
  testCase,
  index,
  onEdit,
  onDelete
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: testCase.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <Card ref={setNodeRef} style={style} className={isDragging ? 'shadow-lg' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CardTitle className="text-base">Test Case #{index + 1}</CardTitle>
                {testCase.difficulty && (
                  <Badge 
                    variant={testCase.difficulty === 'hard' ? 'destructive' : 
                            testCase.difficulty === 'medium' ? 'secondary' : 'outline'}
                  >
                    {testCase.difficulty}
                  </Badge>
                )}
                {testCase.category && (
                  <Badge variant="outline">{testCase.category}</Badge>
                )}
              </div>
              {testCase.tags && testCase.tags.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {testCase.tags.map((tag: string, idx: number) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onEdit(testCase)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Test Case</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this test case? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => onDelete(testCase)}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-4">
        <div>
          <div className="text-sm font-medium text-muted-foreground mb-2">Question</div>
          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm">{testCase.question}</p>
          </div>
        </div>
        
        <div>
          <div className="text-sm font-medium text-muted-foreground mb-2">Expected Answer</div>
          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm">{testCase.goldenAnswer}</p>
          </div>
        </div>
        
        {testCase.evaluations && testCase.evaluations.length > 0 && (
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-2">
              Recent Evaluations ({testCase.evaluations.length})
            </div>
            <div className="text-sm text-muted-foreground">
              Last run: {testCase.evaluations[0]?.judgeScore?.toFixed(1) || 'N/A'} score
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}